import { useState, useEffect } from "react";
import { Radar, CloudLightning, QrCode, Receipt, Factory, Volume2, Radio, HeartPulse, BarChart3, Smartphone, Play, ShieldCheck, AlertTriangle, Thermometer, Droplets, Wind, Loader2, ArrowLeft, RefreshCw, LogOut, LogIn, Mail, Rocket, Eye, Truck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { ACCESS_WORD } from "@/lib/accessKey";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, SA_ORG_KEY } from "@/lib/api";
import { armDemo, launchDemo, launchFleetDemo, armFleetDemo, PRESENT_KEY } from "@/lib/showcaseTour";
import { localToday } from "@/lib/dates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LeadForm } from "@/pages/VaultEntrance";
import Flyer from "@/pages/Flyer";

const ARMOR = "#0052CC";
const CONTACT = "admin@armorbatch.com";
const accessMailto = `mailto:${CONTACT}?subject=${encodeURIComponent("ArmorBatch Global — access request")}&body=${encodeURIComponent(
  "I'd like access to ArmorBatch Global.\n\nName:\nCompany:\nPhone:\nCountry / time zone:\nPlants:\nTrucks:\n")}`;

const SAMPLE_WX = { temp_c: 38.4, humidity_pct: 22, wind_kph: 42 };
const SAMPLE_REASONS = [
  "Air temperature 38.4°C exceeds 35°C — rapid surface drying",
  "Relative humidity 22% below 30% — high evaporation rate",
  "Wind 42 km/h exceeds 30 km/h — plastic shrinkage risk",
];

function WeatherGuardDemo({ open, onClose }) {
  const [name, setName] = useState("");
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-surface border-red-600" data-testid="showcase-quality-guard-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase font-black text-red-500 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Quality Guard · Shrinkage-Crack Warning</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 mono text-[11px]">
          {[[Thermometer, `${SAMPLE_WX.temp_c}°C`, "Temp"], [Droplets, `${SAMPLE_WX.humidity_pct}%`, "Humidity"], [Wind, `${SAMPLE_WX.wind_kph} km/h`, "Wind"]].map(([I, v, l]) => (
            <div key={l} className="border border-red-600/50 bg-red-500/5 p-2 text-center"><I className="w-4 h-4 mx-auto text-red-400" /><div className="text-ink font-black text-base mt-1">{v}</div><div className="text-[9px] uppercase tracking-widest text-muted-industrial">{l}</div></div>
          ))}
        </div>
        <p className="text-sm">Current plant conditions exceed AS 1379 hot-weather thresholds. Pouring now carries a high risk of plastic-shrinkage cracking — apply evaporation retarder, add fog spray, or postpone the pour.</p>
        <ul className="text-sm list-disc pl-5 space-y-0.5" data-testid="showcase-quality-guard-reasons">{SAMPLE_REASONS.map((r) => <li key={r}>{r}</li>)}</ul>
        <div className="border border-red-600/50 bg-red-500/5 p-3 text-xs">By accepting, the customer acknowledges the evaporation-retarder / postponement advice and accepts liability for plastic-shrinkage cracking. This acceptance is logged on every docket for this order.</div>
        <div>
          <div className="label-tag">Accepted by (customer / site contact name)</div>
          <Input data-testid="showcase-quality-guard-ack-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. John Smith — Site Foreman" className="rounded-none bg-transparent border-industrial" />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="showcase-quality-guard-cancel">Postpone / Cancel</Button>
          <Button className="rounded-none bg-red-600 hover:bg-red-500 text-white" disabled={!name.trim()} data-testid="showcase-quality-guard-accept"
            onClick={() => { toast.success(`Risk accepted by ${name} — logged on every docket for this order`); onClose(); }}>Accept Risk & Create Order</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Showcase() {
  const [busy, setBusy] = useState(null);
  const { user, logout, demoLogin, fleetDemoLogin } = useAuth();
  const nav = useNavigate();
  const [wxOpen, setWxOpen] = useState(false);
  const isPublic = !user;   // the vault door lands the public here
  useEffect(() => { sessionStorage.removeItem(PRESENT_KEY); }, []);

  // Public visitors no longer roam the live sample plant — they only see the
  // print-ready flyer + contact details. Logged-in staff still get the full demo
  // showcase below (kept for showing clients in person).
  if (isPublic) return <Flyer />;

  const run = async (key, fn) => {
    setBusy(key);
    try {
      if (!user) await demoLogin();   // public visitor: sign into the shared read-only demo first
      await fn();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not launch demo"); } finally { setBusy(null); }
  };

  const runFleet = async () => {
    setBusy("fleet");
    try {
      if (user?.is_super_admin) {
        // Super admin: enable + switch org context into TasMulch, no logout.
        await armFleetDemo();
        window.location.assign("/war-room");
        return;
      }
      if (!user) await fleetDemoLogin();
      else { try { await logout(); } catch { /* ignore */ } await fleetDemoLogin(); }
      await launchFleetDemo();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not launch the Fleet Dispatch demo"); setBusy(null); }
  };

  const CARDS = [
    {
      key: "war-room", Icon: Radar, title: "War Room Dispatch", tag: "Live operations",
      blurb: "Three-column command view — today's jobs, the live fleet with AI turnaround ETAs, and a real-time traffic map. Fourteen sample trucks are already rolling.",
      points: ["Live GPS map & traffic", "One-touch BATCH to plant", "Safety lockout & fleet health"],
      launch: () => run("war-room", () => launchDemo("war-room")),
    },
    {
      key: "weather", Icon: CloudLightning, title: "Weather Guard", tag: "Quality protection",
      blurb: "Quality Guard reads live plant weather and blocks risky pours. When heat, wind or low humidity trip AS 1379 thresholds, the dispatcher sees the Shrinkage-Crack warning and the customer signs off the risk.",
      points: ["Hot-weather thresholds", "Customer risk sign-off", "Logged on every docket"],
      launch: () => setWxOpen(true),
    },
    {
      key: "pump", Icon: QrCode, title: "Pump Tracker", tag: "Site visibility",
      blurb: "The pump operator scans a QR code on site and watches every truck for their pour approach in real time — ETAs, load sizes and slump, no login required.",
      points: ["Scan-and-go QR link", "Live truck ETAs per site", "Nothing to install"],
      launch: () => run("pump", async () => {
        const w = window.open("", "_blank");   // open synchronously so iPad/Safari doesn't block the popup
        await armDemo();
        const { data } = await api.get(`/dashboard?date=${localToday()}`);
        const job = (data.jobs || []).find((j) => (j.assigned_truck_ids || []).length) || (data.jobs || [])[0];
        if (!job) throw new Error("no job");
        const links = await api.get(`/jobs/${job.id}/pitch-links`);
        const url = `${window.location.origin}${links.data.pump_path}`;
        if (w) w.location = url; else window.location.assign(url);
      }),
    },
    {
      key: "invoicing", Icon: Receipt, title: "Auto-Invoicing", tag: "60-second billing",
      blurb: "At the end of the day every delivered docket is already priced — one click groups them per account customer, applies fuel surcharge and wait time, and issues the invoices. Sixty seconds, not sixty minutes.",
      points: ["One invoice per customer", "Xero / MYOB / QBO ready", "Docket-accurate pricing"],
      launch: () => run("invoicing", () => launchDemo("invoicing")),
    },
    {
      key: "dispatch", Icon: Radio, title: "Classic Dispatch", tag: "Before vs after",
      blurb: "The traditional dispatch board — a job list, a truck list and a lot of phone calls. Open it side-by-side with the War Room and the difference speaks for itself.",
      points: ["Same jobs, same fleet", "Manual truck assignment", "Compare with War Room"],
      launch: () => run("dispatch", () => launchDemo("dispatch")),
    },
    {
      key: "fleet-health", Icon: HeartPulse, title: "Fleet Health", tag: "Safety & compliance",
      blurb: "Every truck's pre-start check, failed items, service hours and defects in one ranked board. Sample fleet includes a failed brake check and overdue services so the alerts are live.",
      points: ["Daily pre-start compliance", "Service-due alerts", "Defect & maintenance history"],
      launch: () => run("fleet-health", () => launchDemo("fleet-health")),
    },
    {
      key: "daily-report", Icon: BarChart3, title: "Daily Report", tag: "End-of-day summary",
      blurb: "Today's plant in one page — m³ ordered vs delivered, dockets, wait-time revenue, water top-ups and invoices. What the manager reads before locking the gate.",
      points: ["Ordered vs delivered m³", "Wait-time & top-up revenue", "Print or email in one click"],
      launch: () => run("daily-report", () => launchDemo("daily-report")),
    },
    {
      key: "driver", Icon: Smartphone, title: "Driver App", tag: "In-cab · water warning",
      blurb: "The driver's phone view of the live docket: one-tap status, site water top-up and the customer signature. Add more than 20 L/m³ and the AS 1379 water warning appears before the customer signs — try 200 L on the sample load.",
      points: ["One-tap depart / arrive / discharge", "Site water top-up log", "AS 1379 >20 L/m³ warning & sign-off"],
      launch: () => run("driver", () => launchDemo("driver")),
    },
    {
      key: "cockpit", Icon: Factory, title: "One-Touch Batching", tag: "Plant cockpit · live audio", wide: true,
      blurb: "Two lanes, live scale weights and every additive dosing in real time. Watch the tolerance bars land, then hear the plant call \"Load Complete\" the moment every weight is on target and stable — no timers, no guesswork.",
      points: ["Live silo, bin & admix gauges", "Sensor-driven Load Complete", "Spoken alert for the operator"],
      launch: () => run("cockpit", async () => {
        try { window.speechSynthesis.speak(new SpeechSynthesisUtterance(" ")); } catch { /* unlock audio on tap */ }
        await launchDemo("cockpit");
      }),
    },
  ];

  return (
    <div className="min-h-screen bg-background text-ink" data-testid="showcase-page">
      <div className="flex items-center justify-between px-4 py-3 border-b border-industrial">
        {isPublic ? (
          <>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-industrial" data-testid="showcase-public-mark">ArmorBatch Global · The Global Standard</div>
            <div className="flex items-center gap-2">
              <a href={accessMailto} data-testid="showcase-contact-top"
                className="mono text-[10px] uppercase tracking-widest px-3 py-2 border border-industrial hover:border-[#0052CC] hover:text-[#0052CC] inline-flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Contact us
              </a>
              <Link to={`/login?key=${ACCESS_WORD}`} data-testid="showcase-login-top"
                className="mono text-[10px] uppercase tracking-widest px-3 py-2 text-white inline-flex items-center gap-2 hover:opacity-90" style={{ background: ARMOR }}>
                <LogIn className="w-3.5 h-3.5" /> Log in
              </Link>
            </div>
          </>
        ) : (<>
        {user?.demo_only
          ? <div className="flex items-center gap-4">
              <Link to="/war-room" className="mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-ink inline-flex items-center gap-1" data-testid="showcase-back"><ArrowLeft className="w-3.5 h-3.5" /> Console</Link>
              <button type="button" onClick={async () => { await logout(); nav("/login"); }} className="mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-ink inline-flex items-center gap-1" data-testid="showcase-signout"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
            </div>
          : <Link to="/war-room" className="mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-ink inline-flex items-center gap-1" data-testid="showcase-back"><ArrowLeft className="w-3.5 h-3.5" /> Console</Link>}
        <button type="button" disabled={busy === "reset"} data-testid="showcase-reset-btn"
          onClick={() => run("reset", async () => { await api.post("/demo/reset"); await armDemo(); toast.success("Sample plant reset — fresh fleet, full silos, today's jobs"); })}
          className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-widest px-3 py-2 border border-industrial hover:border-[#0052CC] hover:text-[#0052CC] disabled:opacity-60">
          {busy === "reset" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Reset sample plant
        </button>
        </>)}
      </div>
      <header className="text-center pt-6 pb-8 sm:pt-10 sm:pb-12 px-4" data-testid="showcase-brand">
        <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center border-2" style={{ borderColor: ARMOR, background: `linear-gradient(135deg, ${ARMOR}, #003F9E)` }}>
          <ShieldCheck className="w-9 h-9 sm:w-11 sm:h-11 text-white" />
        </div>
        <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none mt-5" style={{ color: ARMOR }} data-testid="showcase-brand-name">ArmorBatch Global</h1>
        <div className="mono text-xs sm:text-sm uppercase tracking-[0.35em] text-muted-industrial mt-3" data-testid="showcase-slogan">The Global Standard</div>
        {isPublic
          ? <p className="text-sm sm:text-base text-muted-industrial max-w-2xl mx-auto mt-5" data-testid="showcase-public-blurb">
              Nine live demonstrations of a working concrete plant — dispatch, batching, delivery and invoicing in one console.
              Tap <span className="text-ink font-bold">Watch Demo</span> on any screen — sample plant data, nothing to set up.
            </p>
          : <p className="text-sm sm:text-base text-muted-industrial max-w-2xl mx-auto mt-5">Nine live demonstrations, each pre-loaded with sample plant data. Tap <span className="text-ink font-bold">Launch Demo</span> — nothing to set up.</p>}
        {!isPublic && (
          <div className="mt-7 flex justify-center">
            <button type="button" onClick={runFleet} disabled={!!busy} data-testid="showcase-fleet-demo-hero"
              className="inline-flex items-center justify-center gap-2.5 text-white text-base font-bold px-8 py-4 transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: "#228B40" }}>
              {busy === "fleet" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Truck className="w-5 h-5" />} Try Fleet Dispatch Demo
            </button>
          </div>
        )}
        {isPublic && (
          <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <button type="button" onClick={() => run("war-room", () => launchDemo("war-room"))} disabled={!!busy} data-testid="showcase-demo-hero"
              className="inline-flex items-center justify-center gap-2.5 text-white text-base font-bold px-8 py-4 transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: ARMOR }}>
              {busy === "war-room" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />} Watch the Live Demo
            </button>
            <button type="button" onClick={runFleet} disabled={!!busy} data-testid="showcase-fleet-demo-hero"
              className="inline-flex items-center justify-center gap-2.5 text-white text-base font-bold px-8 py-4 transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: "#228B40" }}>
              {busy === "fleet" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Truck className="w-5 h-5" />} Try Fleet Dispatch Demo
            </button>
            <Link to="/signup" data-testid="showcase-trial-hero"
              className="inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-black text-white mono text-xs font-bold uppercase tracking-widest px-6 py-4 transition-colors">
              <Rocket className="w-4 h-4" /> Start 14-day trial
            </Link>
            <Link to="/tour" data-testid="showcase-tour-hero"
              className="inline-flex items-center justify-center gap-2 border border-industrial hover:border-[#0052CC] text-ink mono text-xs font-bold uppercase tracking-widest px-6 py-4 transition-colors">
              <Eye className="w-4 h-4" /> Watch the tour
            </Link>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 px-4 pb-10 max-w-6xl mx-auto" data-testid="showcase-cards">
        {CARDS.map(({ key, Icon, title, tag, blurb, points, launch, wide }) => (
          <article key={key} className={`relative bg-surface border border-industrial p-6 sm:p-8 flex flex-col group hover:border-[#0052CC] transition-colors duration-200 ${wide ? "md:col-span-2" : ""}`} data-testid={`showcase-card-${key}`}>
            <div className="absolute top-0 left-0 h-1 w-full" style={{ background: ARMOR }} />
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 shrink-0 flex items-center justify-center border border-industrial group-hover:border-[#0052CC] transition-colors" style={{ color: ARMOR }}><Icon className="w-7 h-7" /></div>
              <div className="min-w-0">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-industrial">{tag}</div>
                <h2 className="heading text-2xl sm:text-3xl font-black uppercase leading-tight text-ink mt-1">{title}</h2>
              </div>
            </div>
            <p className="text-sm sm:text-base text-muted-industrial mt-5 leading-relaxed">{blurb}</p>
            <ul className="mt-4 space-y-1.5">
              {points.map((p) => <li key={p} className="mono text-xs uppercase tracking-wider text-ink flex items-center gap-2"><span className="w-1.5 h-1.5" style={{ background: ARMOR }} />{p}</li>)}
            </ul>
            {wide && <div className="mt-4 inline-flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-500/50 px-2 py-1 self-start" data-testid="showcase-audio-hint"><Volume2 className="w-3.5 h-3.5" /> Turn the volume up — a load completes about every minute</div>}
            <button type="button" onClick={launch} disabled={busy === key} data-testid={`showcase-launch-${key}`}
              className="mt-8 w-full sm:w-auto sm:self-start inline-flex items-center justify-center gap-2 px-6 py-4 sm:py-3 text-white heading font-black uppercase tracking-widest text-base sm:text-sm disabled:opacity-60 active:scale-[0.98] transition-transform"
              style={{ background: ARMOR }}>
              {busy === key ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />} {isPublic ? "Watch Demo" : "Launch Demo"}
            </button>
          </article>
        ))}
      </div>

      {isPublic && (
        <footer className="border-t border-industrial px-4 py-12" data-testid="showcase-public-footer">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-ink">Secure your plant</h2>
              <p className="text-sm text-muted-industrial mt-4 max-w-md">
                Every screen above, unlimited users, unlimited dockets. Tell us about your operation and we'll
                open a private demo and quote you properly.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href={accessMailto} data-testid="showcase-contact-footer"
                  className="inline-flex items-center justify-center gap-2.5 text-white text-base font-bold px-7 py-3.5"
                  style={{ background: ARMOR }}>
                  <Mail className="w-5 h-5" /> Contact us
                </a>
                <Link to="/signup" data-testid="showcase-trial-footer"
                  className="inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-black text-white mono text-xs font-bold uppercase tracking-widest px-6 py-3.5">
                  <Rocket className="w-4 h-4" /> Start 14-day trial
                </Link>
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mt-6">{CONTACT}</div>
            </div>
            <div className="[&_.mono]:text-muted-industrial [&_input]:bg-transparent [&_input]:border-industrial [&_input]:!text-ink [&_input]:placeholder:text-muted-industrial">
              <LeadForm dark />
            </div>
          </div>
        </footer>
      )}

      <WeatherGuardDemo open={wxOpen} onClose={() => setWxOpen(false)} />
    </div>
  );
}
